<template>
  <WizardContainer
    width="792px"
    height="624px"
  >
    <WizardContent
      v-if="loading"
    >
      <div class="ma-auto">
        <v-progress-circular
          indeterminate
          color="brandPrimary"
          size="40"
        />
      </div>
    </WizardContent>
    <WizardContent
      v-else
      :icon-url="iconUrl"
    >
      <template #flashMessage>
        <EduWarningFlashMessage :email="email" />
      </template>
      <h4 class="mt-6 mb-8">
        Join the {{ projectName }} Dashboard
      </h4>
      <v-row
        v-if="!ssoMandatory || ssoIsGoogle || ssoIsMicrosoft"
        class="ga-4"
        no-gutters
      >
        <DpplGoogleLinkedinButton
          v-if="!ssoMandatory || ssoIsGoogle"
          class="google-linkedin-button mb-6"
          :href="googleUrl"
        >
          <img
            src="@/assets/icon-google.svg"
            width="64px"
            height="20px"
            alt="Authenticate with Google"
          >
        </DpplGoogleLinkedinButton>
        <DpplGoogleLinkedinButton
          v-if="!ssoMandatory || ssoIsMicrosoft"
          class="google-linkedin-button mb-6"
          :href="microsoftUrl"
        >
          <img
            class="ml-1 mr-3"
            src="@/assets/icon-microsoft.svg"
            alt="Authenticate with Microsoft"
          >
          Sign-in with Microsoft
        </DpplGoogleLinkedinButton>
      </v-row>
      <DpplGoogleLinkedinButton
        v-if="ssoName && !(ssoIsGoogle || ssoIsMicrosoft)"
        class="sso-button mb-6 ml-4 w-100"
        color="primary"
        variant="outlined"
        title="Single Sign-On"
        @click="onSsoClick"
      >
        Single Sign-On
      </DpplGoogleLinkedinButton>
      <div v-if="!ssoMandatory">
        <DpplPageBreak
          text="OR"
          class="mb-4"
        />
        <v-form
          ref="form"
          v-model="valid"
          @submit.prevent="submitForm"
        >
          <div
            class="flex-grow-1 d-flex align-center justify-space-between"
          >
            <DpplSignupFormHalfTextField
              v-model="firstName"
              label="First Name"
              :rules="firstNameRules"
              validate-on="blur"
            />
            <DpplSignupFormHalfTextField
              v-model="lastName"
              label="Last Name"
              :rules="lastNameRules"
              validate-on="blur"
              margin="0px 0px 0px 24px"
            />
          </div>
          <DpplTextField
            v-model="email"
            label="Company Email"
            :rules="emailRules"
            validate-on="blur"
          />
          <DpplTextField
            v-model="password"
            class="mb-3"
            label="Password"
            :rules="passwordRules"
            type="password"
            autocomplete="off"
            validate-on="blur"
            data-sentry-mask
          />
          <DpplSignupFormButton
            class="mb-4"
            type="submit"
            block
            :loading="loading"
          >
            Continue
          </DpplSignupFormButton>
        </v-form>
      </div>
      <WizardFooter>
        <h5>
          Already have an account?
          <router-link
            class="pl-2"
            to="login"
          >
            Sign In
          </router-link>
        </h5>
      </WizardFooter>
    </WizardContent>
  </WizardContainer>
</template>

<script>
import WizardContainer from '@/components/shared/WizardContainer.vue';
import WizardContent from '@/components/shared/WizardContent.vue';
import WizardFooter from '@/components/shared/WizardFooter.vue';
import {
  signupBuildAuthUrls, signupGetProjectNameAndImage, signupJoinDashboard,
} from '@/api/signup';
import validation from '@/helpers/validation';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import DpplGoogleLinkedinButton from '@/components/datapeople/DpplGoogleLinkedinButton.vue';
import DpplSignupFormHalfTextField from '@/components/datapeople/DpplSignupFormHalfTextField.vue';
import DpplPageBreak from '@/components/datapeople/DpplPageBreak.vue';
import EduWarningFlashMessage from '@/components/shared/EduWarningFlashMessage.vue';
import { mapActions } from 'pinia';
import { useWizard } from '@/stores/wizard/wizard';

export default {
  name: 'JoinDashboard',
  components: {
    WizardContainer,
    WizardContent,
    WizardFooter,
    DpplTextField,
    DpplSignupFormButton,
    DpplGoogleLinkedinButton,
    DpplSignupFormHalfTextField,
    DpplPageBreak,
    EduWarningFlashMessage,
  },
  emits: ['track-page-load'],
  data() {
    return {
      projectName: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      firstNameRules: validation.firstNameRules(),
      lastNameRules: validation.lastNameRules(),
      emailRules: validation.emailRules(),
      passwordRules: validation.passwordRules(),
      inviteKey: this.$route.query.key,
      valid: false,
      loading: false,
      googleUrl: null,
      microsoftUrl: null,
      iconUrl: null,
      ssoName: '',
      ssoMandatory: false,
      ssoIsGoogle: false,
      ssoIsMicrosoft: false,
    };
  },
  head() {
    return {
      title: this.$route.meta.title || 'Datapeople',
      meta: [
        {
          name: 'description',
          content: 'Want to try the Datapeople Smart Editor for Job Descriptions for free? Create'
          + ' a new Datapeople account to begin editing your company’s job descriptions.',
        },
      ],
    };
  },
  async created() {
    if (!this.inviteKey) {
      this.$router.push({ name: 'login' });
    }

    const getLinkedinAndGoogleOauthUrls = async () => {
      const responseJSON = await signupBuildAuthUrls({ inviteKey: this.inviteKey });
      if (responseJSON.success === true) {
        this.googleUrl = responseJSON.google_auth_url;
        this.microsoftUrl = responseJSON.microsoft_auth_url;
      }
    };

    const getProjectNameAndImage = async () => {
      const responseJSON = await signupGetProjectNameAndImage({ key: this.inviteKey });
      this.iconUrl = responseJSON.imageUrl;
      this.projectName = responseJSON.projectName;
      this.ssoName = responseJSON.ssoName || 'sdf';
      this.ssoMandatory = responseJSON.ssoMandatory;
      this.ssoIsGoogle = responseJSON.ssoIsGoogle;
      this.ssoIsMicrosoft = responseJSON.ssoIsMicrosoft;
    };

    try {
      this.loading = true;

      await Promise.all([
        getProjectNameAndImage().catch(() => {
          this.$router.push({ name: 'login' });
        }),
        getLinkedinAndGoogleOauthUrls().catch((e) => {
          this.updateFlash({ message: e.message });
        }),
      ]);
    } finally {
      this.loading = false;
    }
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Join Dashboard' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupJoinDashboard({
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            password: this.password,
            inviteKey: this.inviteKey,
          });
          if (responseJSON.success === true) {
            this.$router.push({
              name: 'terms-of-service',
            });
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Join error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        if (responseJSON.errors && responseJSON.errors.sso_mandatory) {
          if (responseJSON.errors.is_google) {
            window.location.href = '/signup/google';
          } else if (responseJSON.errors.is_microsoft) {
            window.location.href = '/signup/microsoft';
          } else {
            window.location.href = `/login/${responseJSON.errors.provider}`;
          }
          return;
        }
        if (
          responseJSON.errors
          && responseJSON.errors.auth
          && responseJSON.errors.auth.includes('Email does not match invite key.')
        ) {
          this.updateFlash({ message: 'You must use your company email address to register.' });
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
    onSsoClick() {
      window.location.href = `/login/${this.ssoName}`;
    },
  },
};
</script>

<style scoped lang="less">
.row {
  margin-left: 0;
  margin-right: 0;
}

.google-linkedin-button {
  text-transform: initial;
}

.sso-button:deep(.v-btn__content) {
  overflow: hidden;
  text-transform: initial;
  font-size: 16px;
}
</style>
